import { JsonPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Subscription, filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  dynamicEntityRecordsTopic
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { DynamicEntity, DynamicRecord, OrganizationDynamicApiService } from './organization-dynamic-api.service';

@Component({
  standalone: true,
  selector: 'ain-organization-dynamic-records',
  imports: [JsonPipe, FormsModule, TableModule, ButtonModule, InputTextModule],
  template: `
    <section class="feature-card">
      <h2>{{ t('organization.workflow.records.title') }}</h2>
      <label>{{ t('dynamic.selectEntity') }}
        <select (change)="onEntityChange($event)">
          <option value="">{{ t('common.select') }}</option>
          @for (e of entities(); track e.id) {
            <option [value]="e.id">{{ e.displayName }}</option>
          }
        </select>
      </label>
      @if (selectedEntityId()) {
        <p-table [value]="records()">
          <ng-template pTemplate="header">
            <tr><th>Id</th><th>{{ t('dynamic.values') }}</th><th></th></tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.id }}</td>
              <td><code>{{ row.values | json }}</code></td>
              <td class="actions">
                <button pButton size="small" severity="secondary" [label]="t('common.open')" (click)="startEdit(row)"></button>
                <button pButton size="small" severity="danger" [label]="t('common.delete')" (click)="remove(row.id)"></button>
              </td>
            </tr>
          </ng-template>
        </p-table>
        <div class="toolbar">
          <button pButton [label]="t('dynamic.addRecord')" (click)="startCreate()"></button>
        </div>
        @if (editingRecordId() || creating()) {
          <div class="editor">
            @for (field of editableFields(); track $index) {
              <div class="row">
                <input pInputText [(ngModel)]="field.key" [placeholder]="t('columns.name')" />
                <input pInputText [(ngModel)]="field.value" [placeholder]="t('dynamic.value')" />
                <button pButton severity="danger" [label]="t('common.delete')" (click)="removeField($index)"></button>
              </div>
            }
            <div class="toolbar">
              <button pButton severity="secondary" [label]="t('dynamic.addField')" (click)="addField()"></button>
              <button pButton [label]="t('common.save')" (click)="save()"></button>
            </div>
          </div>
        }
      }
    </section>
  `,
  styles: [`
    .feature-card { background: var(--ain-color-surface); border-radius: 16px; padding: 24px; }
    select { margin: 8px 0; }
    .toolbar { margin-top: 10px; display: flex; gap: 8px; }
    .actions { display: flex; gap: 8px; }
    .editor { margin-top: 12px; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; }
    .row { display: grid; grid-template-columns: 1fr 2fr auto; gap: 8px; margin-bottom: 8px; }
  `]
})
export class OrganizationDynamicRecordsComponent implements OnInit, OnDestroy {
  private readonly api = inject(OrganizationDynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly liveUpdateService = inject(LiveUpdateService);
  private liveEventsSubscription: Subscription | null = null;
  private subscribedTopic = '';
  readonly entities = signal<DynamicEntity[]>([]);
  readonly records = signal<DynamicRecord[]>([]);
  readonly selectedEntityId = signal('');
  readonly editingRecordId = signal<string | null>(null);
  readonly creating = signal(false);
  readonly editableFields = signal<Array<{ key: string; value: string }>>([]);

  ngOnInit(): void {
    this.liveEventsSubscription = this.liveUpdateService.events$
      .pipe(filter(event => event.eventType === LiveUpdateEventTypes.dynamicRecordsChanged))
      .subscribe(() => this.refreshSelectedRecords(false));

    this.api.listEntities().subscribe({ next: items => this.entities.set(items.filter(e => e.isActive)) });
  }

  ngOnDestroy(): void {
    this.liveEventsSubscription?.unsubscribe();
    void this.unsubscribeFromTopic();
  }

  onEntityChange(event: Event): void {
    const id = (event.target as HTMLSelectElement).value;
    void this.switchEntity(id);
  }

  private async switchEntity(id: string): Promise<void> {
    await this.unsubscribeFromTopic();
    this.selectedEntityId.set(id);
    if (!id) {
      this.records.set([]);
      return;
    }

    await this.setupLiveSubscription(id);
    this.refreshSelectedRecords();
  }

  startCreate(): void {
    this.creating.set(true);
    this.editingRecordId.set(null);
    this.editableFields.set([{ key: '', value: '' }]);
  }

  startEdit(record: DynamicRecord): void {
    this.creating.set(false);
    this.editingRecordId.set(record.id);
    this.editableFields.set(Object.entries(record.values).map(([key, value]) => ({ key, value: `${value ?? ''}` })));
  }

  addField(): void {
    this.editableFields.update(items => [...items, { key: '', value: '' }]);
  }

  removeField(index: number): void {
    this.editableFields.update(items => items.filter((_, current) => current !== index));
  }

  payload(): Record<string, unknown> {
    return this.editableFields()
      .filter(item => item.key.trim())
      .reduce((acc, item) => {
        acc[item.key.trim()] = item.value;
        return acc;
      }, {} as Record<string, unknown>);
  }

  save(): void {
    const entityId = this.selectedEntityId();
    if (!entityId) return;

    const recordId = this.editingRecordId();
    const request = this.creating()
      ? this.api.createRecord(entityId, this.payload())
      : this.api.updateRecord(entityId, recordId!, this.payload());

    request.subscribe({
      next: () => this.refreshRecords(entityId),
      error: () => this.records.set([])
    });
  }

  remove(recordId: string): void {
    const entityId = this.selectedEntityId();
    if (!entityId) return;
    this.api.deleteRecord(entityId, recordId).subscribe({
      next: () => this.refreshRecords(entityId),
      error: () => this.records.set([])
    });
  }

  refreshRecords(entityId: string): void {
    this.refreshSelectedRecords(false, entityId);
  }

  private refreshSelectedRecords(showLoading = true, entityId = this.selectedEntityId()): void {
    if (!entityId) return;
    void showLoading;

    this.api.listRecords(entityId).subscribe({
      next: page => {
        this.records.set(page.items);
        this.creating.set(false);
        this.editingRecordId.set(null);
      },
      error: () => this.records.set([])
    });
  }

  private async setupLiveSubscription(entityId: string): Promise<void> {
    const tenantId = this.contextService.context()?.tenantId;
    if (!tenantId) return;

    this.subscribedTopic = dynamicEntityRecordsTopic(tenantId, entityId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }

  private async unsubscribeFromTopic(): Promise<void> {
    if (!this.subscribedTopic) return;
    await this.liveUpdateService.unsubscribe(this.subscribedTopic);
    this.subscribedTopic = '';
  }

  t(key: string): string { return this.localization.translate(key); }
}

